import store from "@/store";

export const ErrorService = {
    validationMessageTranslations: {
        "please This field is required": "هذا الحقل مطلوب",
        "A Bad Request, you have made": "البيانات المرسلة غير صحيحة",
        "Due date must be a valid date and cannot be in the future":
            "تاريخ الاستحقاق غير صالح ولا يمكن أن يكون في المستقبل",
        "Can't Add Admins from here": "لا يمكن إضافة مدير من هذه الشاشة",
    },

    /**
     * Handle HTTP errors globally
     * @param {Error} error - The error object from axios
     * @param {string} customMessage - Optional custom message
     */
    handleError(error, customMessage = null) {
        const status = error.response?.status;
        const errorMessage =
            error.response?.data?.result || error.response?.data?.message;

        const validationMessages = this.extractValidationMessages(
            error.response?.data?.errors
        );

        if (validationMessages.length > 0 && status !== 401) {
            store.dispatch("toastification/setToast", {
                message: validationMessages.join("\n"),
                type: "error",
            });
            return;
        }

        let message =
            customMessage || this.getErrorMessage(status, errorMessage);

        // Show toast notification for all errors except 401 (handled in axios interceptor)
        if (status !== 401) {
            store.dispatch("toastification/setToast", {
                message: message,
                type: "error",
            });
        }

        console.error("API Error:", {
            status,
            message: errorMessage,
            url: error.config?.url,
            method: error.config?.method,
        });
    },

    /**
     * Get localized error message based on status code
     * @param {number} status - HTTP status code
     * @param {string} serverMessage - Message from server
     * @returns {string} Localized error message
     */
    getErrorMessage(status, serverMessage = null) {
        // If server provides a message, use it (assuming it's already localized)
        if (serverMessage) {
            return this.translateValidationMessage(serverMessage);
        }

        switch (status) {
            case 400:
                return "طلب غير صحيح - يرجى التحقق من البيانات المرسلة";
            case 401:
                return "غير مخول - يرجى تسجيل الدخول مرة أخرى";
            case 403:
                return "تم رفض الوصول - ليس لديك صلاحية للوصول لهذا المورد";
            case 404:
                return "المورد غير موجود";
            case 409:
                return "تضارب في البيانات - قد يكون المورد موجود بالفعل";
            case 422:
                return "بيانات غير صالحة - يرجى التحقق من المدخلات";
            case 429:
                return "تم تجاوز الحد المسموح من الطلبات - يرجى المحاولة لاحقاً";
            case 500:
                return "خطأ في الخادم - يرجى المحاولة لاحقاً";
            case 502:
                return "خطأ في بوابة الخادم";
            case 503:
                return "الخدمة غير متاحة مؤقتاً";
            case 504:
                return "انتهت مهلة الاتصال بالخادم";
            default:
                return "حدث خطأ غير متوقع - يرجى المحاولة مرة أخرى";
        }
    },

    /**
     * Handle validation errors (typically 422 responses)
     * @param {Object} validationErrors - Object containing field validation errors
     * @returns {string} Formatted validation error message
     */
    handleValidationErrors(validationErrors) {
        if (!validationErrors || typeof validationErrors !== "object") {
            return "أخطاء في التحقق من صحة البيانات";
        }

        const errors = this.extractValidationMessages(validationErrors);
        return errors.join("\n");
    },

    extractValidationMessages(validationErrors) {
        if (!validationErrors || typeof validationErrors !== "object") {
            return [];
        }

        const errors = [];
        for (const [, messages] of Object.entries(validationErrors)) {
            if (Array.isArray(messages)) {
                messages.forEach((msg) => {
                    if (msg) {
                        errors.push(this.translateValidationMessage(msg));
                    }
                });
            } else if (messages) {
                errors.push(this.translateValidationMessage(messages));
            }
        }

        return errors;
    },

    translateValidationMessage(message) {
        if (!message || typeof message !== "string") {
            return message;
        }

        const exactTranslation = this.validationMessageTranslations[message];
        if (exactTranslation) {
            return exactTranslation;
        }

        if (/^The .* field is required\.?$/i.test(message)) {
            return "هذا الحقل مطلوب";
        }

        if (/^The value '.*' is not valid\.?$/i.test(message)) {
            return "القيمة المدخلة غير صالحة";
        }

        return message;
    },

    /**
     * Handle network errors (no response from server)
     * @param {Error} error - Network error
     */
    handleNetworkError(error) {
        let message =
            "خطأ في الاتصال بالشبكة - يرجى التحقق من اتصالك بالإنترنت";

        if (error.code === "ECONNABORTED") {
            message = "انتهت مهلة الاتصال - يرجى المحاولة مرة أخرى";
        }

        store.dispatch("toastification/setToast", {
            message: message,
            type: "error",
        });

        console.error("Network Error:", error.message);
    },
};

export default ErrorService;
